import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Mail, MessageSquare, Phone, Send, ExternalLink, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

/**
 * Canali comunicazione paziente (Email / SMS / WhatsApp Brevo):
 * badge stato reale, consensi per canale (servizio + marketing), invio promemoria
 * e fallback wa.me manuale. Nessun canale è mostrato attivo se non configurato.
 */
export function useMessagingConfig() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/medico/messaging-config"); setCfg(data); }
      catch { setCfg(null); }
    })();
  }, []);
  return cfg;
}

const CHANNELS = [
  { k: "email", label: "Email", icon: Mail, consentKey: "consent_email_service" },
  { k: "sms", label: "SMS", icon: Phone, consentKey: "consent_sms_service" },
  { k: "whatsapp", label: "WhatsApp", icon: MessageSquare, consentKey: "consent_whatsapp_service" },
];

export function ChannelBadges({ cfg }) {
  if (!cfg) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="channel-badges">
      {CHANNELS.map(({ k, label, icon: Icon }) => {
        const ok = cfg[k]?.configured;
        return (
          <span key={k} data-testid={`channel-badge-${k}`} title={ok ? `${label} attivo` : `Manca: ${(cfg[k]?.missing || []).join(", ") || "configurazione"}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${ok ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-slate-100 border-slate-200 text-slate-400"}`}>
            <Icon size={10} /> {label} {ok ? "attivo" : "non configurato"}
          </span>
        );
      })}
      {cfg.dry_run && <span className="text-[9px] font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">DRY-RUN</span>}
    </div>
  );
}

export default function MessagingChannelsPanel({ patient, phone, email, readOnly }) {
  const cfg = useMessagingConfig();
  const [consents, setConsents] = useState(null);
  const [showMarketing, setShowMarketing] = useState(false);
  const pParams = {
    fiscal_code: patient?.fiscal_code || undefined, name: patient?.name || undefined,
    surname: patient?.surname || undefined, birth: patient?.birth || undefined,
  };

  const loadConsents = useCallback(async () => {
    if (!patient?.name && !patient?.fiscal_code) return;
    try { const { data } = await api.get("/medico/patient-channel-consents", { params: pParams }); setConsents(data); }
    catch { setConsents(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.fiscal_code, patient?.name, patient?.surname]);
  useEffect(() => { loadConsents(); }, [loadConsents]);

  const toggleConsent = async (key, val) => {
    try {
      const { data } = await api.put("/medico/patient-channel-consents", {
        patient_fiscal_code: patient?.fiscal_code, patient_name: patient?.name,
        patient_surname: patient?.surname, patient_birth: patient?.birth,
        consents: { [key]: val },
      });
      setConsents(data);
      toast.success(val ? "Consenso registrato" : "Consenso revocato");
    } catch { toast.error("Errore aggiornamento consenso"); }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-3" data-testid="messaging-channels-panel">
      <div className="flex items-center gap-1.5 mb-2">
        <Send size={13} className="text-brand-azure" />
        <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Comunicazioni paziente</span>
      </div>
      <ChannelBadges cfg={cfg} />
      {cfg && !cfg.whatsapp?.configured && (
        <div className="mt-1.5 text-[10px] text-slate-500 leading-tight" data-testid="whatsapp-config-hint">
          WhatsApp non configurato: attiva WhatsApp Business su Brevo e imposta {(cfg.whatsapp?.missing || []).slice(0, 3).join(", ")}.
        </div>
      )}
      {consents && (
        <div className="mt-2 border-t border-slate-100 pt-2" data-testid="channel-consents">
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">Consensi comunicazioni di servizio</div>
          {CHANNELS.map(({ k, label, consentKey }) => (
            <label key={k} className="flex items-center justify-between py-0.5 text-[11px] text-slate-600 cursor-pointer">
              <span>{label} servizio {!consents[consentKey] && <span className="text-amber-600 font-bold">· consenso mancante</span>}</span>
              <input type="checkbox" disabled={readOnly} checked={!!consents[consentKey]}
                onChange={(e) => toggleConsent(consentKey, e.target.checked)}
                data-testid={`consent-toggle-${k}`} className="rounded border-slate-400" />
            </label>
          ))}
          <button type="button" onClick={() => setShowMarketing((x) => !x)} className="text-[9px] text-slate-400 hover:text-slate-600 underline mt-0.5" data-testid="consents-marketing-toggle">
            {showMarketing ? "Nascondi" : "Mostra"} consensi marketing/newsletter
          </button>
          {showMarketing && ["email", "sms", "whatsapp"].map((k) => (
            <label key={k} className="flex items-center justify-between py-0.5 text-[11px] text-slate-500 cursor-pointer">
              <span>Marketing {k}</span>
              <input type="checkbox" disabled={readOnly} checked={!!consents[`consent_marketing_${k}`]}
                onChange={(e) => toggleConsent(`consent_marketing_${k}`, e.target.checked)}
                data-testid={`consent-toggle-marketing-${k}`} className="rounded border-slate-400" />
            </label>
          ))}
        </div>
      )}
      <div className="text-[9px] text-slate-400 mt-1.5 leading-tight">
        Le comunicazioni sanitarie di servizio sono distinte dal marketing. Senza consenso esplicito il canale non invia.
      </div>
    </div>
  );
}

/** Bottoni promemoria multi-canale, disabilitati se canale non configurato o consenso mancante. */
export function ReminderButtons({ patient, phone, email, eventType, context = {}, compact }) {
  const cfg = useMessagingConfig();
  const [consents, setConsents] = useState(null);
  const [busy, setBusy] = useState(null);
  const pParams = {
    fiscal_code: patient?.fiscal_code || undefined, name: patient?.name || undefined,
    surname: patient?.surname || undefined, birth: patient?.birth || undefined,
  };
  useEffect(() => {
    if (!patient?.name && !patient?.fiscal_code) return;
    (async () => {
      try { const { data } = await api.get("/medico/patient-channel-consents", { params: pParams }); setConsents(data); }
      catch { setConsents(null); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.fiscal_code, patient?.name, patient?.surname]);

  const send = async (channel) => {
    setBusy(channel);
    try {
      const { data } = await api.post("/medico/notify-patient", {
        channel, event_type: eventType,
        patient_fiscal_code: patient?.fiscal_code, patient_name: patient?.name,
        patient_surname: patient?.surname, patient_birth: patient?.birth,
        phone, email, ...context,
      });
      if (data.ok) toast.success(data.message);
      else toast.warning(`${data.message}${(data.missing || []).length ? ` — manca: ${data.missing.join(", ")}` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
    finally { setBusy(null); }
  };

  const disabledReason = (k, label, consentKey, target) => {
    if (!cfg?.[k]?.configured) return `${label} non configurato`;
    if (consents && !consents[consentKey]) return "Consenso mancante";
    if (!target) return k === "email" ? "Email paziente mancante" : "Telefono paziente mancante";
    return null;
  };

  const waManualText = encodeURIComponent(
    `Gentile ${patient?.name || "paziente"}, promemoria da PrimeVision${context.data ? ` per il ${context.data}${context.ora ? " ore " + context.ora : ""}` : ""}${context.procedura ? ` (${context.procedura})` : ""}.`
  );
  const waPhoneDigits = (phone || "").replace(/\D/g, "");

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${compact ? "" : "mt-1"}`} data-testid={`reminder-buttons-${eventType}`}>
      {CHANNELS.map(({ k, label, icon: Icon, consentKey }) => {
        const target = k === "email" ? email : phone;
        const reason = disabledReason(k, label, consentKey, target);
        return (
          <button key={k} type="button" disabled={!!reason || busy === k} title={reason || `Invia promemoria via ${label}`}
            data-testid={`reminder-${eventType}-${k}`}
            onClick={() => send(k)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border transition ${reason ? "bg-slate-100 border-slate-300 text-slate-400 cursor-not-allowed" : "bg-white border-brand-azure/40 text-brand-azure hover:bg-brand-azure hover:text-white"}`}>
            <Icon size={10} /> {busy === k ? "…" : label}
          </button>
        );
      })}
      {waPhoneDigits && (
        <a href={`https://wa.me/${waPhoneDigits.startsWith("39") || waPhoneDigits.length > 10 ? waPhoneDigits : "39" + waPhoneDigits}?text=${waManualText}`}
          target="_blank" rel="noreferrer" data-testid={`reminder-${eventType}-wa-manual`}
          title="Apri WhatsApp Web/app con testo precompilato (invio manuale dal medico)"
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border border-emerald-300 text-emerald-700 hover:bg-emerald-50">
          <ExternalLink size={10} /> WhatsApp manuale
        </a>
      )}
      {consents && !consents.consent_sms_service && !consents.consent_whatsapp_service && (
        <span className="inline-flex items-center gap-1 text-[9px] text-amber-600" data-testid={`reminder-${eventType}-consent-warn`}>
          <ShieldAlert size={9} /> consensi canale mancanti
        </span>
      )}
    </div>
  );
}
